import { ReactElement, useState, useMemo, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';

interface WheelCircumferenceData {
  [manufacturer: string]: {
    [name: string]: {
      [innerWidthMm: string]: {
        [pressure: string]: {
          values: number[];
          average: number;
        };
      };
    };
  };
}

interface WheelCircumferenceViewProps {
  data?: WheelCircumferenceData;
  tyres: any[];
}

const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7c7c',
  '#8dd1e1',
  '#d084d0',
  '#ffb347',
  '#67b7dc',
  '#a4de6c',
  '#ffd93d',
];

const WheelCircumferenceView = ({ data, tyres }: WheelCircumferenceViewProps): ReactElement => {
  const [selectedTyres, setSelectedTyres] = useState<Set<string>>(new Set());
  const [showOnlyTyresWithData, setShowOnlyTyresWithData] = useState(true);

  // Group tyres by manufacturer, optionally filtering by data availability
  const tyresByManufacturer = useMemo(() => {
    const grouped: { [key: string]: any[] } = {};
    tyres.forEach((tyre) => {
      const manufacturerName = tyre.manufacturer?.name || 'Unknown';
      const hasData = data && data[manufacturerName]?.[tyre.name];

      // Skip tyres without data if filter is enabled
      if (showOnlyTyresWithData && !hasData) {
        return;
      }

      if (!grouped[manufacturerName]) {
        grouped[manufacturerName] = [];
      }
      grouped[manufacturerName].push(tyre);
    });

    // Remove manufacturers with no tyres after filtering
    Object.keys(grouped).forEach((manufacturer) => {
      if (grouped[manufacturer].length === 0) {
        delete grouped[manufacturer];
      }
    });

    return grouped;
  }, [tyres, data, showOnlyTyresWithData]);

  const handleTyreToggle = (manufacturer: string, tyreName: string) => {
    const key = `${manufacturer}|${tyreName}`;
    const newSelected = new Set(selectedTyres);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedTyres(newSelected);
  };

  const plotRef = useRef<HTMLDivElement>(null);

  // Process data for Plotly
  const plotlyData = useMemo(() => {
    const traces: any[] = [];
    let colorIndex = 0;

    if (!data || selectedTyres.size === 0) {
      return [];
    }

    // Process selected tyres data
    selectedTyres.forEach((tyreKey) => {
      const [manufacturer, name] = tyreKey.split('|');

      if (data[manufacturer]?.[name]) {
        const tyreData = data[manufacturer][name];

        // For each inner width
        Object.entries(tyreData).forEach(([innerWidth, pressureData]) => {
          const seriesName = `${manufacturer} ${name} - Rim Width: ${innerWidth}mm`;
          const currentColor = COLORS[colorIndex % COLORS.length];

          // Collect scatter points and average points separately
          const scatterX: number[] = [];
          const scatterY: number[] = [];
          const averageX: number[] = [];
          const averageY: number[] = [];

          Object.entries(pressureData).forEach(([pressure, data]) => {
            const pressureNum = parseFloat(pressure);

            if (data && typeof data === 'object' && data.values && data.average !== undefined) {
              // New format with server-side averaging
              // Add individual scatter points
              data.values.forEach((value: number) => {
                scatterX.push(pressureNum);
                scatterY.push(value);
              });

              // Add average point
              averageX.push(pressureNum);
              averageY.push(data.average);
            } else if (Array.isArray(data)) {
              // Legacy format - array of values
              data.forEach((value) => {
                scatterX.push(pressureNum);
                scatterY.push(value);
              });
            } else if (typeof data === 'number') {
              // Single value
              scatterX.push(pressureNum);
              scatterY.push(data);
            }
          });

          // Add scatter trace for individual measurements
          if (scatterX.length > 0) {
            traces.push({
              x: scatterX,
              y: scatterY,
              mode: 'markers',
              type: 'scatter',
              name: `${seriesName} (measurements)`,
              marker: {
                color: currentColor,
                size: 4,
                opacity: 0.6,
              },
              showlegend: false,
            });
          }

          // Add line trace for averages
          if (averageX.length > 0) {
            // Sort average points by x value for proper line drawing
            const sortedData = averageX.map((x, i) => ({ x, y: averageY[i] })).sort((a, b) => a.x - b.x);

            traces.push({
              x: sortedData.map((d) => d.x),
              y: sortedData.map((d) => d.y),
              mode: 'lines+markers',
              type: 'scatter',
              name: seriesName,
              line: {
                color: currentColor,
                width: 3,
              },
              marker: {
                color: currentColor,
                size: 8,
                line: {
                  color: '#fff',
                  width: 2,
                },
              },
            });
          }

          colorIndex++;
        });
      }
    });

    return traces;
  }, [data, selectedTyres]);

  // Update plot when data changes
  useEffect(() => {
    if (plotRef.current && plotlyData.length > 0) {
      const layout = {
        title: 'Wheel Circumference vs Pressure',
        xaxis: {
          title: 'Pressure (bar)',
          type: 'linear',
        },
        yaxis: {
          title: 'Wheel Circumference (m)',
        },
        legend: {
          x: 0,
          y: -0.2,
          orientation: 'h',
        },
        margin: {
          l: 60,
          r: 40,
          t: 60,
          b: 100,
        },
        hovermode: 'closest',
      };

      const config = {
        displayModeBar: true,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displaylogo: false,
        responsive: true,
      };

      Plotly.newPlot(plotRef.current, plotlyData, layout, config);
    } else if (plotRef.current && plotlyData.length === 0) {
      // Clear the plot if no data
      Plotly.purge(plotRef.current);
    }
  }, [plotlyData]);

  return (
    <div className="cr-flex cr-h-[600px]">
      {/* Sidebar for tyre selection */}
      <div className="cr-w-80 cr-overflow-y-auto cr-border-r cr-border-gray-200 cr-pr-4">
        <h3 className="cr-mb-4 cr-text-lg cr-font-semibold">Select Tyres</h3>

        {/* Filter option */}
        <div className="cr-mb-4 cr-border-b cr-border-gray-200 cr-pb-4">
          <label className="cr-flex cr-cursor-pointer cr-items-center cr-space-x-2">
            <input
              type="checkbox"
              checked={showOnlyTyresWithData}
              onChange={(e) => setShowOnlyTyresWithData(e.target.checked)}
              className="cr-rounded cr-border-gray-300"
            />
            <span className="cr-text-sm cr-text-gray-700">Show only tyres with data</span>
          </label>
        </div>

        {Object.entries(tyresByManufacturer).map(([manufacturer, manufacturerTyres]) => (
          <div key={manufacturer} className="cr-mb-4">
            <h4 className="cr-mb-2 cr-font-medium cr-text-gray-700">{manufacturer}</h4>
            <div className="cr-ml-4 cr-space-y-1">
              {manufacturerTyres.map((tyre) => {
                const key = `${manufacturer}|${tyre.name}`;
                const isSelected = selectedTyres.has(key);
                const hasData = data && data[manufacturer]?.[tyre.name];

                return (
                  <label
                    key={tyre.id}
                    className={`cr-flex cr-cursor-pointer cr-items-center cr-space-x-2 cr-rounded cr-p-2
                     hover:cr-bg-gray-50
                      ${!hasData ? 'cr-cursor-not-allowed cr-opacity-50' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => hasData && handleTyreToggle(manufacturer, tyre.name)}
                      disabled={!hasData}
                      className="cr-rounded cr-border-gray-300"
                    />
                    <span className="cr-text-sm">
                      {tyre.name}
                      {!hasData && <span className="cr-ml-1 cr-text-xs cr-text-gray-500">(no data)</span>}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Plot area */}
      <div className="cr-flex-1 cr-pl-6">
        <h3 className="cr-mb-4 cr-text-lg cr-font-semibold">Wheel Circumference vs Pressure</h3>

        {selectedTyres.size === 0 ? (
          <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-gray-500">
            <p>Select one or more tyres to view wheel circumference data</p>
          </div>
        ) : plotlyData.length === 0 ? (
          <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-gray-500">
            <p>No data available for selected tyres</p>
          </div>
        ) : (
          <div className="cr-relative cr-h-[520px]">
            {/* Axis labels */}
            <div className="cr-absolute cr--left-14 cr-top-1/2 cr-z-10 cr--translate-y-1/2 cr--rotate-90 cr-transform cr-text-sm cr-font-medium cr-text-gray-700">
              Circumference (m)
            </div>
            <div className="cr-absolute cr-bottom-16 cr-left-1/2 cr-z-10 cr--translate-x-1/2 cr-transform cr-text-sm cr-font-medium cr-text-gray-700">
              Pressure (Bar)
            </div>
            <div ref={plotRef} className="cr-h-full cr-w-full" />
          </div>
        )}
      </div>
    </div>
  );
};

export default WheelCircumferenceView;
